import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export interface NetworkRequest {
  id: string;
  url: string;
  method: string;
  timestamp: Date;
  status?: number;
  duration?: number;
  requestBody?: any;
  responseBody?: any;
  error?: string;
}

export interface SimulatedResponseConfig {
  status: number;
  body?: any;
}

interface DebugContextType {
  requests: NetworkRequest[];
  addRequest: (request: NetworkRequest) => void;
  updateRequest: (id: string, updates: Partial<NetworkRequest>) => void;
  clearRequests: () => void;
  highlightedStyle: string | null;
  setHighlightedStyle: (style: string | null) => void;
  simulatedResponses: Record<string, SimulatedResponseConfig>;
  setSimulatedResponse: (endpointId: string, config: SimulatedResponseConfig | null) => void;
  getSimulatedResponse: (endpointId: string) => SimulatedResponseConfig | null;
}

const DebugContext = createContext<DebugContextType | undefined>(undefined);

// Module-level storage for requests captured before React mounts
let pendingRequests: NetworkRequest[] = [];
let requestListeners: ((requests: NetworkRequest[]) => void)[] = [];

// Module-level function to add requests
const addPendingRequest = (request: NetworkRequest) => {
  pendingRequests = [request, ...pendingRequests].slice(0, 50);
  requestListeners.forEach(listener => listener([...pendingRequests]));
};

// Module-level function to update requests
const updatePendingRequest = (id: string, updates: Partial<NetworkRequest>) => {
  pendingRequests = pendingRequests.map(req => 
    req.id === id ? { ...req, ...updates } : req
  );
  requestListeners.forEach(listener => listener([...pendingRequests]));
};

// Set up fetch interceptor IMMEDIATELY at module load time
const originalFetch = window.fetch;

window.fetch = async (...args) => {
  const [input, init] = args;
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : (input as Request).url;
  const method = init?.method || 'GET';
  
  // Track Supabase API calls and external API calls (DynamicWeb)
  const isTrackedUrl = url.includes('supabase.co') || 
                       url.includes('mydwsite.com') || 
                       url.includes('/dwapi/');
  if (!isTrackedUrl) {
    return originalFetch(...args);
  }

  const requestId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  const startTime = Date.now();
  
  let requestBody: any = undefined;
  if (init?.body) {
    try {
      requestBody = JSON.parse(init.body as string);
    } catch {
      requestBody = init.body;
    }
  }

  // Add initial request
  addPendingRequest({
    id: requestId,
    url,
    method,
    timestamp: new Date(),
    requestBody,
  });

  try {
    const response = await originalFetch(...args);
    const duration = Date.now() - startTime;
    
    // Clone response to read body without consuming it
    const clonedResponse = response.clone();
    let responseBody: any;
    try {
      responseBody = await clonedResponse.json();
    } catch {
      responseBody = await clonedResponse.text();
    }

    updatePendingRequest(requestId, {
      status: response.status,
      duration,
      responseBody,
    });

    return response;
  } catch (error) {
    const duration = Date.now() - startTime;
    updatePendingRequest(requestId, {
      duration,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
    throw error;
  }
};

export const DebugProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [requests, setRequests] = useState<NetworkRequest[]>(() => [...pendingRequests]);
  const [highlightedStyle, setHighlightedStyle] = useState<string | null>(null);
  const [simulatedResponses, setSimulatedResponses] = useState<Record<string, SimulatedResponseConfig>>({});

  const setSimulatedResponse = useCallback((endpointId: string, config: SimulatedResponseConfig | null) => {
    setSimulatedResponses(prev => {
      if (!config) {
        const { [endpointId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [endpointId]: config };
    });
  }, []);

  const getSimulatedResponse = useCallback((endpointId: string): SimulatedResponseConfig | null => {
    return simulatedResponses[endpointId] || null;
  }, [simulatedResponses]);

  // Subscribe to request updates
  useEffect(() => {
    const listener = (newRequests: NetworkRequest[]) => {
      setRequests(newRequests);
    };
    requestListeners.push(listener);
    
    // Sync any requests that came in before this effect ran
    setRequests([...pendingRequests]);
    
    return () => {
      requestListeners = requestListeners.filter(l => l !== listener);
    };
  }, []);

  const addRequest = useCallback((request: NetworkRequest) => {
    addPendingRequest(request);
  }, []);

  const updateRequest = useCallback((id: string, updates: Partial<NetworkRequest>) => {
    updatePendingRequest(id, updates);
  }, []);

  const clearRequests = useCallback(() => {
    pendingRequests = [];
    setRequests([]);
  }, []);

  return (
    <DebugContext.Provider value={{ 
      requests, 
      addRequest, 
      updateRequest, 
      clearRequests,
      highlightedStyle,
      setHighlightedStyle,
      simulatedResponses,
      setSimulatedResponse,
      getSimulatedResponse,
    }}>
      {children}
    </DebugContext.Provider>
  );
};

export const useDebug = () => {
  const context = useContext(DebugContext);
  if (!context) {
    throw new Error('useDebug must be used within a DebugProvider');
  }
  return context;
};
